<template>
  <b-card no-body class="p-2">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <h5 class="mb-0">Export History</h5>
      <small v-if="refreshStateText" class="text-muted">{{ refreshStateText }}</small>
    </div>
    <p v-if="loading" class="mb-0">Loading export list...</p>
    <p v-else-if="error" class="mb-0 text-danger">{{ error }}</p>
    <b-table
      v-else
      small
      responsive="md"
      primary-key="id"
      :items="tableItems"
      :fields="tableFields"
      empty-text="No exports found."
      class="mb-0"
    >
      <template #cell(status)="row">
        <div class="d-inline-flex align-items-center">
          <b-badge :variant="statusVariant(row.item.status)">
            {{ formatStatus(row.item.status) }}
          </b-badge>
          <span
            v-if="shouldShowStatusDetails(row.item)"
            v-b-tooltip.hover
            :title="statusDetailsTooltip(row.item)"
            class="ml-1 text-muted d-inline-flex align-items-center"
            tabindex="0"
          >
            <b-icon icon="info-circle" />
          </span>
        </div>
      </template>
      <template #cell(actions)="row">
        <b-dropdown
          size="sm"
          variant="outline-primary"
          text="Action"
          right
        >
          <b-dropdown-item
            :disabled="!canDownload(row.item)"
            @click="handleDownload(row.item)"
          >
            Download
          </b-dropdown-item>
          <b-dropdown-item @click="openDeleteModal(row.item)">
            Delete
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </b-table>
    <ExportDeleteModal
      :visible.sync="deleteModalVisible"
      :delete-candidate="deleteCandidate"
      :delete-in-flight="deleteInFlight"
      @confirm="handleDeleteConfirm"
      @hidden="handleDeleteModalHidden"
    />
  </b-card>
</template>

<script>
import ExportDeleteModal from '@/xvisor/components/export/ExportDeleteModal.vue';
import {
  canDownloadExport,
  EXPORT_STORAGE_FULL_CODE,
  formatStatus,
  hasPendingExports,
  mapExportItemsToTableItems,
  normalizeErrorField,
  normalizeStatus,
  shouldShowStatusDetails,
  statusDetailsTooltip,
  statusVariant,
} from '@/xvisor/components/export/exportFormatters';
import { deleteExport, downloadExport, listExports } from '@/xvisor/components/export/exportApi';

export default {
  components: {
    ExportDeleteModal,
  },
  data() {
    return {
      loading: true,
      error: '',
      exports: [],
      pollTimeoutId: null,
      requestPromise: null,
      isRefreshing: false,
      lastUpdatedAt: null,
      pollIntervalMs: 2000,
      pollRetryIntervalMs: 5000,
      deleteCandidate: null,
      deleteModalVisible: false,
      deleteInFlight: false,
      tableFields: [
        { key: 'requestedAt', label: 'Requested At' },
        { key: 'status', label: 'Status' },
        { key: 'targetIndex', label: 'Data Source' },
        { key: 'startAt', label: 'Start Time' },
        { key: 'endAt', label: 'End Time' },
        {
          key: 'numRecords',
          label: 'Records',
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'numBytes',
          label: 'Bytes',
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        { key: 'actions', label: 'Action' },
      ],
    };
  },
  computed: {
    refreshStateText() {
      if (this.loading) return '';
      if (this.isRefreshing) return 'Refreshing...';
      if (!this.lastUpdatedAt) return '';
      return `Last updated ${this.lastUpdatedAt.toLocaleTimeString()}`;
    },
    tableItems() {
      return mapExportItemsToTableItems(this.exports);
    },
  },
  mounted() {
    this.httpGet();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    canDownload: canDownloadExport,
    formatStatus,
    shouldShowStatusDetails,
    statusDetailsTooltip,
    statusVariant,
    stopPolling() {
      if (this.pollTimeoutId) {
        clearTimeout(this.pollTimeoutId);
        this.pollTimeoutId = null;
      }
    },
    hasPendingExports(items = this.exports) {
      return hasPendingExports(items);
    },
    schedulePoll(delayMs) {
      if (this.pollTimeoutId) return;
      this.pollTimeoutId = setTimeout(() => {
        this.pollTimeoutId = null;
        this.httpGet({ isPoll: true });
      }, delayMs);
    },
    syncPolling(nextExports) {
      if (this.hasPendingExports(nextExports)) {
        this.schedulePoll(this.pollIntervalMs);
      } else {
        this.stopPolling();
      }
    },
    maybeNotifyStatusTransitions(previousExports, nextExports) {
      if (!this.$bvToast) return;

      const previousStatusById = {};
      previousExports.forEach((exportItem) => {
        previousStatusById[exportItem.id] = normalizeStatus(exportItem.status);
      });

      nextExports.forEach((exportItem) => {
        const previousStatus = previousStatusById[exportItem.id];
        const nextStatus = normalizeStatus(exportItem.status);
        if (previousStatus !== 'pending') return;
        if (nextStatus === 'ready') {
          this.$bvToast.toast('An export is ready for download.', {
            variant: 'success',
          });
          return;
        }
        if (nextStatus === 'failed') {
          const errorCode = normalizeErrorField(exportItem.errorCode);
          if (errorCode === EXPORT_STORAGE_FULL_CODE) {
            this.$bvToast.toast(
              'An export failed: export storage is full. Delete old exports and retry.',
              { variant: 'danger' },
            );
            return;
          }
          this.$bvToast.toast('An export failed to process.', {
            variant: 'danger',
          });
        }
      });
    },
    getDownloadFilename(exportItem, headers) {
      const contentDisposition = headers
        ? (headers['content-disposition'] || headers['Content-Disposition'])
        : '';
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (matches && matches[1]) {
          return matches[1];
        }
      }
      return 'export.ndjson.gz';
    },
    async handleDownload(exportItem) {
      if (!this.canDownload(exportItem)) {
        if (this.$bvToast) {
          this.$bvToast.toast('This export is not ready for download.', {
            variant: 'warning',
          });
        }
        return;
      }
      try {
        const response = await downloadExport(this.$http, exportItem.id);
        const filename = this.getDownloadFilename(exportItem, response.headers);
        const fileBlob = new Blob(
          [response.data],
          { type: response.data.type || 'application/gzip' },
        );
        const downloadUrl = window.URL.createObjectURL(fileBlob);
        const downloadLink = document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(downloadUrl);
      } catch (err) {
        const status = err && err.response ? err.response.status : null;
        if (!this.$bvToast) return;
        if (status === 404) {
          this.$bvToast.toast('This export is not ready for download.', {
            variant: 'warning',
          });
          return;
        }
        if (status === 410) {
          this.$bvToast.toast(
            'This export is from the pre-gzip format and is no longer downloadable. '
            + 'Please create a new export.',
            { variant: 'warning' },
          );
          return;
        }
        this.$bvToast.toast('Failed to download export.', {
          variant: 'danger',
        });
      }
    },
    openDeleteModal(exportItem) {
      this.deleteCandidate = { ...exportItem };
      this.deleteModalVisible = true;
    },
    handleDeleteModalHidden() {
      this.deleteModalVisible = false;
      if (!this.deleteInFlight) {
        this.deleteCandidate = null;
      }
    },
    async handleDeleteConfirm() {
      if (!this.deleteCandidate || this.deleteInFlight) {
        return;
      }

      this.deleteInFlight = true;
      const exportId = this.deleteCandidate.id;
      try {
        await deleteExport(this.$http, exportId);
        if (this.$bvToast) {
          this.$bvToast.toast('Export deleted.', { variant: 'success' });
        }
        this.deleteModalVisible = false;
        this.deleteCandidate = null;
        await this.httpGet();
      } catch (err) {
        const status = err && err.response ? err.response.status : null;
        const responseMessage = err
          && err.response
          && err.response.data
          && typeof err.response.data.message === 'string'
          ? err.response.data.message
          : '';
        if (!this.$bvToast) return;
        if (status === 404) {
          this.$bvToast.toast('Export no longer exists.', { variant: 'warning' });
          await this.httpGet();
          this.deleteModalVisible = false;
          this.deleteCandidate = null;
          return;
        }
        if (status === 409) {
          this.$bvToast.toast(
            responseMessage || 'Export is still processing and cannot be deleted.',
            { variant: 'warning' },
          );
          return;
        }
        this.$bvToast.toast('Failed to delete export.', { variant: 'danger' });
      } finally {
        this.deleteInFlight = false;
      }
    },
    async fetchExportsFromApi() {
      return listExports(this.$http);
    },
    async httpGet({ isPoll = false } = {}) {
      if (this.requestPromise) {
        return this.requestPromise;
      }

      if (isPoll) {
        this.isRefreshing = true;
      } else {
        this.loading = true;
        this.error = '';
      }

      const previousExports = this.exports.slice();
      this.requestPromise = this.fetchExportsFromApi()
        .then((data) => {
          const nextExports = Array.isArray(data) ? data : [];
          this.exports = nextExports;
          this.lastUpdatedAt = new Date();
          this.maybeNotifyStatusTransitions(previousExports, nextExports);
          this.syncPolling(nextExports);
        })
        .catch((err) => {
          if (isPoll) {
            if (this.hasPendingExports()) {
              this.schedulePoll(this.pollRetryIntervalMs);
            }
            return;
          }
          this.error = `Failed to load exports: ${err.message}`;
        })
        .finally(() => {
          this.loading = false;
          this.isRefreshing = false;
          this.requestPromise = null;
        });

      return this.requestPromise;
    },
    reload() {
      return this.httpGet();
    },
  },
};
</script>
